import React from 'react'
import {Button, SplitColumnsContainer} from 'startlibs/lib/components'
import {withForm, Field, Errors, TextInput} from 'startlibs/lib/form'
import {withToggles, withRouteTo} from 'startlibs/lib/hocs'
import load from 'little-loader'
import {STRIPE_KEY} from '../config/StripeConfig'
import {CreditCardIcon, CreditCardWrapper, StripeInput} from '../checkout/CheckoutComponents'
import {AJAX_ERROR, postFetcher} from 'startlibs'
import {ice} from 'icepath'
import {buildValidation, minLength, required} from '../lib/validation'
import {setNotifications} from '../reducers'
import {connect} from 'react-redux'
import {executeCaptcha, RecaptchaBrandingText, waitCaptcha} from "../checkout/CaptchaUtils";

@withForm()
@connect(undefined,{setNotifications})
@withRouteTo
@withToggles('loading')
export class CreditCardUpdate extends React.PureComponent {

  state = {}

  cardErrors = {
    cardCvc: {message: 'Your card\'s security code is incomplete.'},
    cardExpiry: {message: 'Your card\'s expiration date is incomplete.'},
    card: {message: 'Your card number is incomplete.'}
  }

  preValidation = buildValidation({
      cardholder: required,
      country: required,
      zipCode: [required, minLength(5)]
    }
  )
  captchaRef = {current:null}

  componentDidMount() {
    Promise.all([this.loadStripe(),waitCaptcha((token) => this.doSubmit(token),this.captchaRef)])
      .then(([Stripe,captcha]) =>
          this.setState({stripe: Stripe(STRIPE_KEY)})
      )
    window.scroll(0, 0)
  }

  componentDidUpdate(oldProps, oldState) {
    if (oldState.stripe !== this.state.stripe) {
      const elements = this.state.stripe.elements()
      const style = {
        base: {
          color: '#3c3c3c',
          lineHeight: '18px',
          fontFamily: '"Open Sans", sans-serif',
          fontSize: '14px',
          '::placeholder': {
            color: 'rgba(0,0,0,0.35)'
          }
        },
        invalid: {
          color: '#C3282D',
          iconColor: '#fa755a'
        }
      }

      const cardExpiry = elements.create('cardExpiry', {style})
      cardExpiry.mount('#card-expiry-element')
      cardExpiry.on('change', ({error}) => {
        this.cardModified = true
        this.cardErrors = {...this.cardErrors, cardExpiry: error}
      })

      const cardCvc = elements.create('cardCvc', {style})
      cardCvc.mount('#card-cvc-element')
      cardCvc.on('change', ({error}) => {
        this.cardModified = true
        this.cardErrors = {...this.cardErrors, cardCvc: error}
      })

      this.card = elements.create('cardNumber', {style})
      this.card.on('change', (event) => {
        this.setState({cardBrand: event.brand})
        this.cardModified = true
        this.cardErrors = {...this.cardErrors, card: event.error}
      })
      this.card.mount('#card-element')
    }
  }

  loadStripe = () =>
    window.Stripe ? Promise.resolve(window.Stripe) : new Promise((res, rej) =>
      load('https://js.stripe.com/v3/', (err) => {
        if (err) { rej() }
        res(window.Stripe)
      })
    )

  stripeResult = null
  doSubmit = (captchaToken) => {
    return postFetcher('/api/update-stripe-token', {
      token: this.stripeResult.token.id,
      captchaToken
    }, {method: 'PUT'})
      .then(() => {
        this.props.setNotifications('Credit card successfully updated')
        this.props.routeTo('/billing')
      })
      .catch((e) => {
        this.props.loading.close()
        this.props.form.connector.setErrors({"card":[e && e.errorResponse ? e.errorResponse.detail || e.errorResponse.title : AJAX_ERROR]})
      })
  }

  handleSubmit = (e) => {
    e.preventDefault()
    const {loading, form} = this.props
    const {stripe} = this.state
    const validated = this.preValidation(form.properties, {})
    const cardErrors = ice(this.cardErrors).filter((v) => v).map((v) => [v.message]).value()
    const stripeValidation =
      Object.keys(cardErrors).length ?
      stripe.createToken(this.card).then((result) => ({card: [result.error.message]})) :
      Promise.resolve({})

    if (validated.errors) {
      stripeValidation.then((stripeErrors) => form.connector.setErrors({...validated.errors, ...cardErrors}))
      return
    }
    loading.open()
    const values = form.properties
    stripe.createToken(this.card, {
        address_zip: values.zipCode,
        address_country: values.country,
        name: values.cardholder
      })
      .then((stripeResult) => {
        if (stripeResult.error) {
          // Inform the customer that there was an error.
          if (stripeResult.error) {
            form.connector.setErrors({card: [stripeResult.error.message]})
          } else {
            form.connector.setErrors({form: [AJAX_ERROR]})
          }
          loading.close()
        } else {
          // Send the token to your server.
          this.stripeResult = stripeResult
          executeCaptcha(values)
        }
      }).catch((e) => {
      loading.close()
      form.connector.setErrors({form: [AJAX_ERROR]})
    })
  }

  render() {
    const {form, loading} = this.props
    const {cardBrand} = this.state
    return <div className="content edit-profile">
      <div className={'profile-header checkout-header'}>
        <h1>Change credit card</h1>
      </div>
      <form>
        <CreditCardWrapper>
          <div className='expanded-card'>
            <div className="update-card-heading">
              <div className="heading-text">
                <h3>Credit card details</h3>
                <p>By changing your credit card, <b>all subscriptions will be charged according to the new registered card</b>.</p>
              </div>
            </div>
            <div className="card billing-card">
              <TextInput
                form={form}
                autoFocus
                type="text"
                path="cardholder"
                label="Name on card:"
                placeholder="Ex: John Doe"
              />
              <Field label="Card number:">
                <StripeInput id="card-element"/>
                <CreditCardIcon src={`/public/icon/${cardBrand || 'unknown'}.svg`}/>
              </Field>
              <SplitColumnsContainer>
                <Field label="Expiration:">
                  <StripeInput id="card-expiry-element"/>
                </Field>
                <Field label="CVC:">
                  <StripeInput id="card-cvc-element"/>
                </Field>
              </SplitColumnsContainer>
              <SplitColumnsContainer>
                <TextInput
                  form={form}
                  type="text"
                  path="country"
                  label="Country:"
                  placeholder="Ex: USA"
                />
                <TextInput
                  form={form}
                  type="text"
                  path="zipCode"
                  label="Zip code:"
                  placeholder="Ex: 21401"
                />
              </SplitColumnsContainer>
            </div>
          </div>
        </CreditCardWrapper>
        <Errors form={form}/>
      </form>
      <div className="profile-footer">
        <Button onClick={() => window.history.back()}>Cancel</Button>
        <Button highlight isLoading={loading.isOpen} onClick={this.handleSubmit}>Update</Button>
      </div>
      <div
        ref={(el) => this.captchaRef.current = el}
        className="g-recaptcha"
      />
      <div>
        <RecaptchaBrandingText>
          This site is protected by reCAPTCHA and the Google <a
          href="https://policies.google.com/privacy"
          rel="noopener noreferrer"
          target="_blank"
        >
          Privacy Policy</a> and <a
          href="https://policies.google.com/terms"
          rel="noopener noreferrer"
          target="_blank"
        >Terms of Service</a> apply.
        </RecaptchaBrandingText>
      </div>
    </div>
  }

}



// WEBPACK FOOTER //
// ./src/javascripts/user/CreditCardUpdate.jsx